/**
 * Product delivery estimate.
 *
 * Required markup:
 * - data-product-delivery-message: wrapper element
 * - data-delivery-cutoff: HH:mm cutoff time, e.g. "14:00"
 * - data-delivery-days: number of business days after dispatch
 * - data-delivery-dispatch-days: comma-separated booleans, Monday to Sunday
 * - data-delivery-excluded-dates: YYYY-MM-DD dates, separated by commas or whitespace
 */
const DEFAULT_DELIVERY_DAYS = 2;
const JS_SATURDAY_INDEX = 6;
const JS_SUNDAY_INDEX = 0;
const DISPATCH_SUNDAY_INDEX = 6;
const MS_PER_SECOND = 1000;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;
const PAD_LENGTH = 2;
const INTERVAL_MS = 1000;
const COUNTDOWN_WINDOW_HOURS = 12;
const COUNTDOWN_WINDOW_MS = COUNTDOWN_WINDOW_HOURS * SECONDS_PER_HOUR * MS_PER_SECOND;
const DELIVERY_ESTIMATE_INTERVAL_KEY = 'productDeliveryEstimateInterval';
const DATE_KEY_PAD_LENGTH = 2;

const clearDeliveryEstimateInterval = () => {
  if (!window.theme?.[DELIVERY_ESTIMATE_INTERVAL_KEY]) return;
  clearInterval(window.theme[DELIVERY_ESTIMATE_INTERVAL_KEY]);
  window.theme[DELIVERY_ESTIMATE_INTERVAL_KEY] = null;
};

const getDateKey = (date) => {
  const month = String(date.getMonth() + 1).padStart(DATE_KEY_PAD_LENGTH, '0');
  const day = String(date.getDate()).padStart(DATE_KEY_PAD_LENGTH, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const initDeliveryEstimate = () => {
  clearDeliveryEstimateInterval();

  const messageEl = document.querySelector('[data-product-delivery-message]');
  if (!messageEl) return;

  const countdownEl = messageEl.querySelector('[data-product-delivery-countdown]');
  const countdownMessageEl = messageEl.querySelector('[data-product-delivery-countdown-message]');
  const genericMessageEl = messageEl.querySelector('[data-product-delivery-generic-message]');
  const countdownDateEl = messageEl.querySelector('[data-product-delivery-countdown-date]');
  const genericDateEl = messageEl.querySelector('[data-product-delivery-generic-date]');
  if (!countdownEl || !countdownMessageEl || !genericMessageEl || !countdownDateEl || !genericDateEl) return;

  const cutoffStr = messageEl.dataset.deliveryCutoff || '14:00';
  const deliveryDays = parseInt(messageEl.dataset.deliveryDays, 10) || DEFAULT_DELIVERY_DAYS;
  const dispatchDaysStr = messageEl.dataset.deliveryDispatchDays || 'true,true,true,true,true,false,false';
  const excludedDatesStr = messageEl.dataset.deliveryExcludedDates || '';

  // Parse dispatch days: index 0 = Monday, 6 = Sunday
  const dispatchDays = dispatchDaysStr.split(',').map(d => d.trim() === 'true');
  const excludedDates = new Set(
    excludedDatesStr
      .split(/[\s,]+/)
      .map(date => date.trim())
      .filter(date => /^\d{4}-\d{2}-\d{2}$/.test(date))
  );

  const [cutoffHour, cutoffMinute] = cutoffStr.split(':').map(Number);

  const isExcludedDate = (date) => {
    return excludedDates.has(getDateKey(date));
  };

  const isDispatchDay = (date) => {
    // JS getDay(): 0=Sun, 1=Mon ... 6=Sat
    // Our array: 0=Mon, 1=Tue ... 6=Sun
    const jsDay = date.getDay();
    const idx = jsDay === JS_SUNDAY_INDEX ? DISPATCH_SUNDAY_INDEX : jsDay - 1;
    return dispatchDays[idx] && !isExcludedDate(date);
  };

  const isDeliveryDay = (date) => {
    const day = date.getDay();
    return day !== JS_SATURDAY_INDEX && day !== JS_SUNDAY_INDEX && !isExcludedDate(date);
  };

  const addBusinessDays = (startDate, days) => {
    const date = new Date(startDate);
    let remaining = days;
    while (remaining > 0) {
      date.setDate(date.getDate() + 1);
      if (isDeliveryDay(date)) {
        remaining--;
      }
    }
    return date;
  };

  const getCutoffForDate = (date) => {
    const cutoff = new Date(date);
    cutoff.setHours(cutoffHour, cutoffMinute, 0, 0);
    return cutoff;
  };

  const getNextDispatchDate = (startDate) => {
    const date = new Date(startDate);
    while (!isDispatchDay(date)) {
      date.setDate(date.getDate() + 1);
    }
    return date;
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', { month: 'short', day: '2-digit' });
  };

  const formatCountdown = (ms) => {
    if (ms <= 0) return '00:00:00';
    const totalSeconds = Math.floor(ms / MS_PER_SECOND);
    const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR);
    const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
    const seconds = totalSeconds % SECONDS_PER_MINUTE;
    return `${String(hours).padStart(PAD_LENGTH, '0')}:${String(minutes).padStart(PAD_LENGTH, '0')}:${String(seconds).padStart(PAD_LENGTH, '0')}`;
  };

  const update = () => {
    if (!messageEl.isConnected) {
      clearDeliveryEstimateInterval();
      return;
    }

    const now = new Date();
    const cutoffToday = getCutoffForDate(now);
    const isBeforeTodaysCutoff = isDispatchDay(now) && now < cutoffToday;
    const remaining = cutoffToday - now;
    const showCountdown = isBeforeTodaysCutoff && remaining <= COUNTDOWN_WINDOW_MS;

    let dispatchDate = now;
    if (!isBeforeTodaysCutoff) {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      dispatchDate = getNextDispatchDate(tomorrow);
    }

    const deliveryDate = addBusinessDays(dispatchDate, deliveryDays);
    const formattedDeliveryDate = formatDate(deliveryDate);
    countdownDateEl.textContent = formattedDeliveryDate;
    genericDateEl.textContent = formattedDeliveryDate;

    if (showCountdown) {
      countdownEl.textContent = formatCountdown(remaining);
    } else {
      countdownEl.textContent = '';
    }

    countdownMessageEl.classList.toggle('hidden', !showCountdown);
    genericMessageEl.classList.toggle('hidden', showCountdown);
    messageEl.classList.remove('hidden');
  };

  update();
  window.theme = window.theme || {};
  window.theme[DELIVERY_ESTIMATE_INTERVAL_KEY] = setInterval(update, INTERVAL_MS);
};

export default initDeliveryEstimate;
